/**
 * HubSpot CRM Integration
 * Implements comprehensive HubSpot integration with API key and OAuth 2.0 authentication,
 * contact properties synchronization, and email engagement tracking
 */

import { BaseCRMIntegration } from '../base/BaseCRMIntegration';
import { 
  IntegrationConfig, 
  SyncResult,
  FieldMapping,
  Contact,
  Activity,
  OAuthTokens,
  ValidationResult,
  TestResult,
  ConnectionResult
} from '../../../types/integration';
import { APIGateway } from '../../api/APIGateway';
import { AuthenticationService } from '../../api/AuthenticationService';

export interface HubSpotConfig extends IntegrationConfig {
  apiKey?: string;
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
  portalId?: string;
  useOAuth: boolean;
  scopes: string[];
}

export interface HubSpotTokens extends OAuthTokens {
  hubId: string;
  userId: string;
}

export interface HubSpotContact {
  id?: string;
  properties: {
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    jobtitle?: string;
    company?: string;
    hs_lead_status?: string;
    createdate?: string;
    lastmodifieddate?: string;
    [key: string]: any;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface HubSpotActivity {
  id?: string;
  contactId: string;
  activityType: string;
  timestamp: string;
  subject: string;
  body?: string;
  metadata?: {
    emailId?: string;
    campaignId?: string;
    [key: string]: any;
  };
}

export interface HubSpotDeal {
  id?: string;
  properties: {
    dealname?: string;
    amount?: string;
    dealstage?: string;
    pipeline?: string;
    closedate?: string;
    createdate?: string;
    hs_lastmodifieddate?: string;
    hubspot_owner_id?: string;
    [key: string]: any;
  };
  associations?: {
    contacts?: string[];
    companies?: string[];
  };
}

export interface HubSpotCompany {
  id?: string;
  properties: {
    name?: string;
    domain?: string;
    industry?: string;
    phone?: string;
    city?: string;
    state?: string;
    country?: string;
    website?: string;
    createdate?: string;
    hs_lastmodifieddate?: string;
    [key: string]: any;
  };
  associations?: {
    contacts?: string[];
    deals?: string[];
  };
}

export class HubSpotIntegration extends BaseCRMIntegration {
  private apiGateway: APIGateway;
  private authService: AuthenticationService;
  private tokens: HubSpotTokens | null = null;
  private readonly baseUrl = 'https://api.hubapi.com';
  
  constructor(config: HubSpotConfig) {
    super(config);
    this.apiGateway = APIGateway.getInstance();
    this.authService = new AuthenticationService();
  }

  // Required methods from BaseIntegration
  async connect(): Promise<ConnectionResult> {
    this.status = 'configuring';
    try {
      const testResult = await this.testConnection();
      this.status = 'active';
      return {
        success: testResult.success,
        message: testResult.message,
        connectionId: this.config.id,
        capabilities: this.getSupportedFeatures(),
        metadata: { platform: 'hubspot', version: 'v3' }
      };
    } catch (error) {
      this.status = 'error';
      return {
        success: false,
        message: `Connection failed: ${error.message}`,
        connectionId: this.config.id
      };
    }
  }

  private getSupportedFeatures(): string[] {
    return [
      'contacts_sync',
      'activities_logging',
      'custom_properties',
      'deals_sync',
      'companies_sync',
      'api_key_auth',
      'oauth_authentication',
      'field_mapping',
      'conflict_resolution',
      'timeline_events'
    ];
  }

  async disconnect(): Promise<void> {
    this.status = 'inactive';
    this.tokens = null;
  }

  async validateConfig(): Promise<ValidationResult> {
    const config = this.config as HubSpotConfig;
    const errors: string[] = [];

    if (config.useOAuth) {
      if (!config.clientId) {
        errors.push('Client ID is required for OAuth authentication');
      }
      if (!config.clientSecret) {
        errors.push('Client Secret is required for OAuth authentication');
      }
      if (!config.redirectUri) {
        errors.push('Redirect URI is required for OAuth authentication');
      }
    } else {
      if (!config.apiKey) {
        errors.push('API Key is required for API key authentication');
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  /**
   * Initialize OAuth 2.0 authentication flow for HubSpot
   */
  async authenticate(): Promise<ValidationResult> {
    try {
      const config = this.config as HubSpotConfig;
      
      if (!config.useOAuth) {
        return {
          valid: true,
          errors: [],
          warnings: [],
          data: { message: 'Using API key authentication' }
        } as any;
      }
      
      // Generate OAuth authorization URL
      const authUrl = this.buildAuthorizationUrl();
      
      return {
        valid: true,
        errors: [],
        warnings: [],
        data: { authUrl }
      } as any;
    } catch (error) {
      return {
        valid: false,
        errors: [{
          field: 'authentication',
          message: `Authentication failed: ${error.message}`,
          code: 'AUTH_ERROR'
        }],
        warnings: []
      };
    }
  }

  /**
   * Complete OAuth flow with authorization code
   */
  async completeAuthentication(authCode: string): Promise<ValidationResult> {
    try {
      const config = this.config as HubSpotConfig;
      const tokenUrl = `${this.baseUrl}/oauth/v1/token`;
      
      const tokenResponse = await this.apiGateway.processRequest({
        method: 'POST',
        url: tokenUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: config.clientId!,
          client_secret: config.clientSecret!,
          redirect_uri: config.redirectUri!,
          code: authCode
        }).toString()
      });

      if (tokenResponse.status === 200) {
        const tokenData = tokenResponse.data;
        this.tokens = {
          accessToken: tokenData.access_token,
          refreshToken: tokenData.refresh_token,
          tokenType: 'Bearer',
          expiresIn: tokenData.expires_in,
          scope: tokenData.scope,
          hubId: tokenData.hub_id,
          userId: tokenData.user_id,
          expiresAt: new Date(Date.now() + tokenData.expires_in * 1000)
        };
        this.status = 'active';
        
        // Store tokens securely
        await this.storeTokens(this.config.id, this.tokens);
        
        return {
          valid: true,
          errors: [],
          warnings: []
        };
      } else {
        throw new Error(`Token exchange failed: ${tokenResponse.status}`);
      }
    } catch (error) {
      return {
        valid: false,
        errors: [{
          field: 'authentication',
          message: `Authentication completion failed: ${error.message}`,
          code: 'AUTH_ERROR'
        }],
        warnings: []
      };
    }
  }

  /**
   * Test connection to HubSpot
   */
  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    
    try {
      const config = this.config as HubSpotConfig;
      
      if (!config.useOAuth && !config.apiKey) {
        return {
          success: false,
          message: 'No API key or OAuth tokens available',
          duration: Date.now() - startTime,
          details: { error: 'Authentication required' }
        };
      }

      if (config.useOAuth && !this.tokens) {
        const storedTokens = await this.getTokens(this.config.id);
        if (!storedTokens) {
          return {
            success: false,
            message: 'No OAuth tokens found',
            duration: Date.now() - startTime,
            details: { error: 'OAuth authentication required' }
          };
        }
        this.tokens = storedTokens as HubSpotTokens;
      }

      // Test with account info endpoint
      const response = await this.makeAuthenticatedRequest(
        'GET',
        '/account-info/v3/api-usage'
      );

      if (response.status === 200) {
        return {
          success: true,
          message: 'Connection test successful',
          duration: Date.now() - startTime,
          details: { responseTime: response.duration }
        };
      } else {
        throw new Error(`Connection test failed: ${response.status}`);
      }
    } catch (error) {
      return {
        success: false,
        message: `Connection test failed: ${error.message}`,
        duration: Date.now() - startTime,
        details: { error: error.message }
      };
    }
  }

  // Required methods from BaseCRMIntegration

  async syncActivities(activities: any[]): Promise<void> {
    await this.logEmailActivities(activities);
  }

  // Override logEmailActivity to match base class signature (single activity)
  async logEmailActivity(activity: any): Promise<void> {
    await this.logEmailActivities([activity]);
  }

  /**
   * Log multiple email activities to HubSpot (base class override)
   */
  async logEmailActivities(activities: any[]): Promise<void> {
    const result = await this.logEmailActivitiesInternal(activities as Activity[]);
    if (!result.success) {
      throw new Error(`Activity logging failed: ${result.errors.join(', ')}`);
    }
  }

  /**
   * Internal method to log multiple email activities to HubSpot
   */
  private async logEmailActivitiesInternal(activities: Activity[]): Promise<SyncResult> {
    try {
      const hubspotActivities: HubSpotActivity[] = activities.map(this.mapActivityToHubSpotActivity.bind(this));
      const results = [];
      const errors = [];

      for (const activity of hubspotActivities) {
        try {
          const response = await this.makeAuthenticatedRequest(
            'POST',
            '/crm/v3/objects/contacts/timeline',
            {
              contactId: activity.contactId,
              eventType: activity.activityType,
              timestamp: activity.timestamp,
              eventData: {
                subject: activity.subject,
                body: activity.body,
                metadata: activity.metadata
              }
            }
          );
          
          if (response.status >= 200 && response.status < 300) {
            results.push(response.data);
          } else {
            errors.push(`Activity logging failed: ${response.status}`);
          }
        } catch (error) {
          errors.push(`Activity logging error: ${error.message}`);
        }
      }

      return {
        success: errors.length === 0,
        recordsProcessed: activities.length,
        recordsSucceeded: results.length,
        recordsFailed: errors.length,
        data: results,
        errors
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: activities.length,
        recordsSucceeded: 0,
        recordsFailed: activities.length,
        data: [],
        errors: [error.message]
      };
    }
  }

  async getContact(contactId: string): Promise<any | null> {
    try {
      const response = await this.makeAuthenticatedRequest(
        'GET',
        `/crm/v3/objects/contacts/${contactId}?properties=firstname,lastname,email,phone,jobtitle,company,hs_lead_status`
      );

      if (response.status === 200) {
        return this.mapHubSpotContactToContact(response.data);
      } else if (response.status === 404) {
        return null;
      } else {
        throw new Error(`Failed to get contact: ${response.status}`);
      }
    } catch (error) {
      console.error('Error getting contact:', error);
      return null;
    }
  }

  async createContact(contact: Partial<any>): Promise<any> {
    const hubspotContact = this.mapContactToHubSpotContact(contact as Contact);

    const response = await this.makeAuthenticatedRequest(
      'POST',
      '/crm/v3/objects/contacts',
      { properties: hubspotContact.properties }
    );

    if (response.status >= 200 && response.status < 300) {
      return { ...contact, id: response.data.id, externalId: response.data.id };
    } else {
      throw new Error(`Failed to create contact: ${response.status}`);
    }
  }

  async updateContact(contactId: string, updates: Partial<any>): Promise<any> {
    const hubspotUpdates = this.mapContactToHubSpotContact(updates as Contact);

    const response = await this.makeAuthenticatedRequest(
      'PATCH',
      `/crm/v3/objects/contacts/${contactId}`,
      { properties: hubspotUpdates.properties }
    );

    if (response.status >= 200 && response.status < 300) {
      return { ...updates, id: contactId, externalId: contactId };
    } else {
      throw new Error(`Failed to update contact: ${response.status}`);
    }
  }

  async deleteContact(contactId: string): Promise<void> {
    const response = await this.makeAuthenticatedRequest(
      'DELETE',
      `/crm/v3/objects/contacts/${contactId}`
    );

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`Failed to delete contact: ${response.status}`);
    }
  }

  async searchContacts(query: string, limit?: number): Promise<any[]> {
    const searchRequest = {
      filterGroups: [
        {
          filters: [
            {
              propertyName: 'email',
              operator: 'CONTAINS_TOKEN',
              value: query
            }
          ]
        },
        {
          filters: [
            {
              propertyName: 'firstname',
              operator: 'CONTAINS_TOKEN',
              value: query
            }
          ]
        },
        {
          filters: [
            {
              propertyName: 'lastname',
              operator: 'CONTAINS_TOKEN',
              value: query
            }
          ]
        }
      ],
      properties: ['firstname', 'lastname', 'email', 'phone', 'jobtitle', 'company', 'hs_lead_status'],
      limit: limit || 100
    };

    const response = await this.makeAuthenticatedRequest(
      'POST',
      '/crm/v3/objects/contacts/search',
      searchRequest
    );

    if (response.status === 200) {
      const hubspotContacts = response.data.results as HubSpotContact[];
      return hubspotContacts.map(this.mapHubSpotContactToContact.bind(this));
    } else {
      throw new Error(`Contact search failed: ${response.status}`);
    }
  }

  // Override method signatures to match base class
  async syncContacts(options: any): Promise<any> {
    const lastSyncTime = options?.dateRange?.start;
    return await this.syncContactsInternal(lastSyncTime);
  }

  private async syncContactsInternal(lastSyncTime?: Date): Promise<SyncResult> {
    try {
      let url = '/crm/v3/objects/contacts?properties=firstname,lastname,email,phone,jobtitle,company,hs_lead_status&limit=100';
      
      if (lastSyncTime) {
        const timestamp = lastSyncTime.getTime();
        url += `&filterGroups[0][filters][0][propertyName]=lastmodifieddate&filterGroups[0][filters][0][operator]=GTE&filterGroups[0][filters][0][value]=${timestamp}`;
      }

      const response = await this.makeAuthenticatedRequest('GET', url);

      if (response.status !== 200) {
        throw new Error(`Contact sync failed: ${response.status}`);
      }

      const hubspotContacts = response.data.results as HubSpotContact[];
      const contacts = hubspotContacts.map(this.mapHubSpotContactToContact.bind(this));

      return {
        success: true,
        recordsProcessed: contacts.length,
        recordsSucceeded: contacts.length,
        recordsFailed: 0,
        data: contacts,
        errors: []
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: 0,
        recordsSucceeded: 0,
        recordsFailed: 0,
        data: [],
        errors: [error.message]
      };
    }
  }

  // Override getCustomFields to return FieldMapping[]
  async getCustomFields(): Promise<FieldMapping[]> {
    try {
      const response = await this.makeAuthenticatedRequest(
        'GET',
        '/crm/v3/properties/contacts'
      );

      if (response.status !== 200) {
        throw new Error(`Custom fields retrieval failed: ${response.status}`);
      }

      const properties = response.data.results || [];
      return properties
        .filter((property: any) => property.name.startsWith('hs_') === false && !this.isStandardProperty(property.name))
        .map((property: any) => ({
          id: property.name,
          sourceField: property.name,
          targetField: property.name,
          direction: 'bidirectional' as any,
          required: property.fieldType === 'text' ? false : true,
          dataType: this.mapHubSpotFieldTypeToDataType(property.type)
        }));
    } catch (error) {
      console.error('Error retrieving custom fields:', error);
      return [];
    }
  }

  private isStandardProperty(propertyName: string): boolean {
    const standardProperties = [
      'firstname', 'lastname', 'email', 'phone', 'jobtitle', 'company',
      'createdate', 'lastmodifieddate', 'hs_lead_status'
    ];
    return standardProperties.includes(propertyName);
  }

  private mapHubSpotFieldTypeToDataType(hsType: string): 'string' | 'number' | 'date' | 'boolean' | 'object' | 'array' {
    const typeMap: { [key: string]: 'string' | 'number' | 'date' | 'boolean' | 'object' | 'array' } = {
      'string': 'string',
      'number': 'number',
      'date': 'date',
      'datetime': 'date',
      'bool': 'boolean',
      'enumeration': 'string',
      'phone_number': 'string',
      'json': 'object'
    };
    
    return typeMap[hsType] || 'string';
  }

  // Abstract method implementations from base class
  protected extractWebhookEventType(payload: any): string {
    return payload.subscriptionType || payload.eventType || 'unknown';
  }

  protected async handleContactWebhook(payload: any): Promise<void> {
    const contactId = payload.objectId || payload.contactId;
    if (contactId) {
      await this.syncContactFromCRM(contactId);
    }
  }

  protected async handleContactDeletedWebhook(payload: any): Promise<void> {
    const contactId = payload.objectId || payload.contactId;
    if (contactId) {
      await this.emitEvent('contact.deleted', { contactId, source: 'hubspot' });
    }
  }

  protected async updateEmailPlatformContact(contact: any): Promise<any> {
    // This would integrate with the email platform's API
    // For now, we'll just return the contact as-is
    return contact;
  }

  // Private helper methods

  private buildAuthorizationUrl(): string {
    const config = this.config as HubSpotConfig;
    const scopes = config.scopes.join(' ');
    
    const params = new URLSearchParams({
      client_id: config.clientId!,
      redirect_uri: config.redirectUri!,
      scope: scopes,
      response_type: 'code'
    });

    return `https://app.hubspot.com/oauth/authorize?${params.toString()}`;
  }

  private async makeAuthenticatedRequest(method: string, endpoint: string, data?: any) {
    const config = this.config as HubSpotConfig;
    const url = `${this.baseUrl}${endpoint}`;
    
    let headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (config.useOAuth && this.tokens) {
      headers['Authorization'] = `Bearer ${this.tokens.accessToken}`;
    } else if (config.apiKey) {
      headers['Authorization'] = `Bearer ${config.apiKey}`;
    } else {
      throw new Error('No authentication method available');
    }

    return this.apiGateway.processRequest({
      method: method as any,
      url,
      headers,
      body: data
    });
  }

  private mapHubSpotContactToContact(hsContact: HubSpotContact): Contact {
    return {
      id: hsContact.id,
      externalId: hsContact.id,
      firstName: hsContact.properties.firstname || '',
      lastName: hsContact.properties.lastname || '',
      email: hsContact.properties.email || '',
      phone: hsContact.properties.phone || '',
      title: hsContact.properties.jobtitle || '',
      company: hsContact.properties.company || '',
      source: hsContact.properties.hs_lead_status || '',
      createdAt: hsContact.properties.createdate ? new Date(hsContact.properties.createdate) : new Date(),
      updatedAt: hsContact.properties.lastmodifieddate ? new Date(hsContact.properties.lastmodifieddate) : new Date(),
      customFields: this.extractCustomFields(hsContact)
    };
  }

  private mapContactToHubSpotContact(contact: Contact): HubSpotContact {
    const properties: any = {
      firstname: contact.firstName,
      lastname: contact.lastName,
      email: contact.email,
      phone: contact.phone,
      jobtitle: contact.title,
      company: contact.company,
      hs_lead_status: contact.source
    };

    // Add custom fields
    if (contact.customFields) {
      Object.assign(properties, contact.customFields);
    }

    return {
      id: contact.externalId,
      properties
    };
  }

  private mapActivityToHubSpotActivity(activity: Activity): HubSpotActivity {
    return {
      contactId: activity.contactId,
      activityType: this.mapActivityType(activity.type),
      timestamp: activity.date.toISOString(),
      subject: activity.subject,
      body: activity.description,
      metadata: {
        emailId: activity.emailMessageId,
        campaignId: activity.campaignId,
        ...activity.metadata
      }
    };
  }

  private mapActivityType(type: string): string {
    const typeMap: { [key: string]: string } = {
      'email_open': 'EMAIL_OPENED',
      'email_click': 'EMAIL_CLICKED',
      'email_sent': 'EMAIL_SENT',
      'email_bounce': 'EMAIL_BOUNCED',
      'email_unsubscribe': 'EMAIL_UNSUBSCRIBED'
    };
    
    return typeMap[type] || 'EMAIL_EVENT';
  }

  private extractCustomFields(hsContact: HubSpotContact): { [key: string]: any } {
    const customFields: { [key: string]: any } = {};
    
    Object.keys(hsContact.properties).forEach(key => {
      if (!this.isStandardProperty(key) && !key.startsWith('hs_')) {
        customFields[key] = hsContact.properties[key];
      }
    });
    
    return customFields;
  }

  /**
   * Refresh OAuth tokens when they expire
   */
  private async refreshTokens(): Promise<void> {
    if (!this.tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    const config = this.config as HubSpotConfig;
    const tokenUrl = `${this.baseUrl}/oauth/v1/token`;
    
    const response = await this.apiGateway.processRequest({
      method: 'POST',
      url: tokenUrl,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.clientId!,
        client_secret: config.clientSecret!,
        refresh_token: this.tokens.refreshToken
      }).toString()
    });

    if (response.status === 200) {
      this.tokens = { ...this.tokens, ...response.data };
      await this.storeTokens(this.config.id, this.tokens);
    } else {
      throw new Error(`Token refresh failed: ${response.status}`);
    }
  }

  // Deal synchronization methods

  /**
   * Get a deal by ID from HubSpot
   */
  async getDeal(dealId: string): Promise<HubSpotDeal | null> {
    try {
      const response = await this.makeAuthenticatedRequest(
        'GET',
        `/crm/v3/objects/deals/${dealId}?properties=dealname,amount,dealstage,pipeline,closedate,hubspot_owner_id&associations=contacts,companies`
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return null;
      } else {
        throw new Error(`Failed to get deal: ${response.status}`);
      }
    } catch (error) {
      console.error('Error getting deal:', error);
      return null;
    }
  }

  /**
   * Create a new deal in HubSpot
   */
  async createDeal(deal: Partial<HubSpotDeal>): Promise<HubSpotDeal> {
    const response = await this.makeAuthenticatedRequest(
      'POST',
      '/crm/v3/objects/deals',
      { properties: deal.properties }
    );

    if (response.status >= 200 && response.status < 300) {
      const createdDeal = response.data;
      
      // Create associations if provided
      if (deal.associations) {
        await this.createDealAssociations(createdDeal.id, deal.associations);
      }
      
      return createdDeal;
    } else {
      throw new Error(`Failed to create deal: ${response.status}`);
    }
  }

  /**
   * Update an existing deal in HubSpot
   */
  async updateDeal(dealId: string, updates: Partial<HubSpotDeal>): Promise<HubSpotDeal> {
    const response = await this.makeAuthenticatedRequest(
      'PATCH',
      `/crm/v3/objects/deals/${dealId}`,
      { properties: updates.properties }
    );

    if (response.status >= 200 && response.status < 300) {
      return response.data;
    } else {
      throw new Error(`Failed to update deal: ${response.status}`);
    }
  }

  /**
   * Delete a deal from HubSpot
   */
  async deleteDeal(dealId: string): Promise<void> {
    const response = await this.makeAuthenticatedRequest(
      'DELETE',
      `/crm/v3/objects/deals/${dealId}`
    );

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`Failed to delete deal: ${response.status}`);
    }
  }

  /**
   * Sync deals from HubSpot
   */
  async syncDeals(lastSyncTime?: Date): Promise<SyncResult> {
    try {
      let url = '/crm/v3/objects/deals?properties=dealname,amount,dealstage,pipeline,closedate,hubspot_owner_id&limit=100';
      
      if (lastSyncTime) {
        const timestamp = lastSyncTime.getTime();
        url += `&filterGroups[0][filters][0][propertyName]=hs_lastmodifieddate&filterGroups[0][filters][0][operator]=GTE&filterGroups[0][filters][0][value]=${timestamp}`;
      }

      const response = await this.makeAuthenticatedRequest('GET', url);

      if (response.status !== 200) {
        throw new Error(`Deal sync failed: ${response.status}`);
      }

      const deals = response.data.results as HubSpotDeal[];

      return {
        success: true,
        recordsProcessed: deals.length,
        recordsSucceeded: deals.length,
        recordsFailed: 0,
        data: deals,
        errors: []
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: 0,
        recordsSucceeded: 0,
        recordsFailed: 0,
        data: [],
        errors: [error.message]
      };
    }
  }

  // Company synchronization methods

  /**
   * Get a company by ID from HubSpot
   */
  async getCompany(companyId: string): Promise<HubSpotCompany | null> {
    try {
      const response = await this.makeAuthenticatedRequest(
        'GET',
        `/crm/v3/objects/companies/${companyId}?properties=name,domain,industry,phone,city,state,country,website&associations=contacts,deals`
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return null;
      } else {
        throw new Error(`Failed to get company: ${response.status}`);
      }
    } catch (error) {
      console.error('Error getting company:', error);
      return null;
    }
  }

  /**
   * Create a new company in HubSpot
   */
  async createCompany(company: Partial<HubSpotCompany>): Promise<HubSpotCompany> {
    const response = await this.makeAuthenticatedRequest(
      'POST',
      '/crm/v3/objects/companies',
      { properties: company.properties }
    );

    if (response.status >= 200 && response.status < 300) {
      const createdCompany = response.data;
      
      // Create associations if provided
      if (company.associations) {
        await this.createCompanyAssociations(createdCompany.id, company.associations);
      }
      
      return createdCompany;
    } else {
      throw new Error(`Failed to create company: ${response.status}`);
    }
  }

  /**
   * Update an existing company in HubSpot
   */
  async updateCompany(companyId: string, updates: Partial<HubSpotCompany>): Promise<HubSpotCompany> {
    const response = await this.makeAuthenticatedRequest(
      'PATCH',
      `/crm/v3/objects/companies/${companyId}`,
      { properties: updates.properties }
    );

    if (response.status >= 200 && response.status < 300) {
      return response.data;
    } else {
      throw new Error(`Failed to update company: ${response.status}`);
    }
  }

  /**
   * Delete a company from HubSpot
   */
  async deleteCompany(companyId: string): Promise<void> {
    const response = await this.makeAuthenticatedRequest(
      'DELETE',
      `/crm/v3/objects/companies/${companyId}`
    );

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`Failed to delete company: ${response.status}`);
    }
  }

  /**
   * Sync companies from HubSpot
   */
  async syncCompanies(lastSyncTime?: Date): Promise<SyncResult> {
    try {
      let url = '/crm/v3/objects/companies?properties=name,domain,industry,phone,city,state,country,website&limit=100';
      
      if (lastSyncTime) {
        const timestamp = lastSyncTime.getTime();
        url += `&filterGroups[0][filters][0][propertyName]=hs_lastmodifieddate&filterGroups[0][filters][0][operator]=GTE&filterGroups[0][filters][0][value]=${timestamp}`;
      }

      const response = await this.makeAuthenticatedRequest('GET', url);

      if (response.status !== 200) {
        throw new Error(`Company sync failed: ${response.status}`);
      }

      const companies = response.data.results as HubSpotCompany[];

      return {
        success: true,
        recordsProcessed: companies.length,
        recordsSucceeded: companies.length,
        recordsFailed: 0,
        data: companies,
        errors: []
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: 0,
        recordsSucceeded: 0,
        recordsFailed: 0,
        data: [],
        errors: [error.message]
      };
    }
  }

  // Association management methods

  /**
   * Create associations between a deal and other objects
   */
  private async createDealAssociations(dealId: string, associations: { contacts?: string[]; companies?: string[] }): Promise<void> {
    if (associations.contacts && associations.contacts.length > 0) {
      await this.createAssociations('deals', dealId, 'contacts', associations.contacts);
    }
    
    if (associations.companies && associations.companies.length > 0) {
      await this.createAssociations('deals', dealId, 'companies', associations.companies);
    }
  }

  /**
   * Create associations between a company and other objects
   */
  private async createCompanyAssociations(companyId: string, associations: { contacts?: string[]; deals?: string[] }): Promise<void> {
    if (associations.contacts && associations.contacts.length > 0) {
      await this.createAssociations('companies', companyId, 'contacts', associations.contacts);
    }
    
    if (associations.deals && associations.deals.length > 0) {
      await this.createAssociations('companies', companyId, 'deals', associations.deals);
    }
  }

  /**
   * Generic method to create associations between objects
   */
  private async createAssociations(fromObjectType: string, fromObjectId: string, toObjectType: string, toObjectIds: string[]): Promise<void> {
    const associationRequests = toObjectIds.map(toObjectId => ({
      from: { id: fromObjectId },
      to: { id: toObjectId },
      type: this.getAssociationType(fromObjectType, toObjectType)
    }));

    const response = await this.makeAuthenticatedRequest(
      'POST',
      `/crm/v3/associations/${fromObjectType}/${toObjectType}/batch/create`,
      { inputs: associationRequests }
    );

    if (response.status < 200 || response.status >= 300) {
      console.error(`Failed to create associations: ${response.status}`);
    }
  }

  /**
   * Get the association type ID for HubSpot associations
   */
  private getAssociationType(fromObjectType: string, toObjectType: string): string {
    // HubSpot association type IDs - these are standard HubSpot values
    const associationTypes: { [key: string]: string } = {
      'deals_to_contacts': '3',
      'contacts_to_deals': '4',
      'deals_to_companies': '5',
      'companies_to_deals': '6',
      'companies_to_contacts': '1',
      'contacts_to_companies': '2'
    };
    
    const key = `${fromObjectType}_to_${toObjectType}`;
    return associationTypes[key] || '1';
  }

  // Token storage methods (temporary implementation)
  private async storeTokens(integrationId: string, tokens: any): Promise<void> {
    // Store tokens in memory for now - in production this would use secure storage
    (this.authService as any)._tokenCache = (this.authService as any)._tokenCache || new Map();
    (this.authService as any)._tokenCache.set(integrationId, tokens);
  }

  private async getTokens(integrationId: string): Promise<any> {
    // Get tokens from memory - in production this would use secure storage
    const cache = (this.authService as any)._tokenCache;
    return cache ? cache.get(integrationId) : null;
  }
}